import React, { useMemo, useState } from 'react';
import { Loader2, X, AlertTriangle } from 'lucide-react';
import { Invoice, InvoiceLine } from '../../types';
import { Button } from '../Button';
import { buildCorrectionLines, validateCorrection } from '../../services/creditNoteLogic';
import { calculateInvoiceTotals } from '../../services/invoiceLogic';
import { centsFromEuroInput, formatCents, formatCentsPlain } from '../../services/money';

/**
 * Korrekturrechnung und kaufmaennische Gutschrift.
 *
 * Beide erzeugen einen EIGENEN Beleg mit eigener Nummer; der Ursprungsbeleg
 * bleibt unveraendert bestehen. Unter GoBD wird nicht ueberschrieben — erst
 * beide Belege zusammen ergeben den richtigen Betrag, und genau das ist der
 * pruefbare Zustand.
 *
 * Der Unterschied der beiden Wege ist nicht kosmetisch:
 *   - Korrektur: der Nutzer gibt an, wie es RICHTIG heissen muss. Der Beleg
 *     traegt danach die Differenz, nicht den neuen Gesamtbetrag.
 *   - Gutschrift: der Nutzer gibt an, was er ERSTATTEN will. Der Betrag wird
 *     als solcher uebernommen und vom Service gespiegelt.
 * Wer das verwechselt, erstattet den vollen Rechnungsbetrag statt der
 * Differenz — deshalb sind es zwei getrennte Modi und kein Schalter.
 */

type Mode = 'correction' | 'credit';

interface Props {
    invoice: Invoice;
    busy: boolean;
    /**
     * Der Fehler der letzten Ausstellung — etwa die Sperre am Rechnungstor
     * (S5a Stufe 3b). Er steht hier, weil der Fehlerbalken der Maske hinter
     * der Abdunklung dieses Dialogs liegt.
     */
    fehler?: string | null;
    onClose: () => void;
    onSubmitCorrection: (correctedLines: InvoiceLine[], reason: string) => void;
    onSubmitCredit: (creditLines: InvoiceLine[], reason: string) => void;
}

export const CorrectionDialog: React.FC<Props> = ({
    invoice,
    busy,
    fehler,
    onClose,
    onSubmitCorrection,
    onSubmitCredit,
}) => {
    const [mode, setMode] = useState<Mode>('correction');
    const [reason, setReason] = useState('');

    const serviceLines = useMemo(
        () => invoice.lines.filter((l) => l.kind === 'SERVICE'),
        [invoice.lines],
    );

    /** Eingaben je Zeile, als Text — der Nutzer tippt deutsche Betraege. */
    const [amounts, setAmounts] = useState<Record<string, string>>(() =>
        Object.fromEntries(serviceLines.map((l) => [l.id, formatCentsPlain(l.netCents)])),
    );

    const parsed = useMemo(() => {
        const out = new Map<string, number | null>();
        for (const l of serviceLines) out.set(l.id, centsFromEuroInput(amounts[l.id] ?? ''));
        return out;
    }, [amounts, serviceLines]);

    const unreadable = serviceLines.filter((l) => parsed.get(l.id) == null);

    /**
     * Im Korrekturmodus sind die eingegebenen Betraege der SOLL-Zustand; die
     * Differenz rechnet buildCorrectionLines. Im Gutschriftmodus sind sie der
     * zu erstattende Betrag und gehen unveraendert weiter — das Spiegeln macht
     * der Service, damit das Vorzeichen an genau einer Stelle entsteht.
     */
    const previewLines = useMemo(() => {
        const edited: InvoiceLine[] = serviceLines.map((l) => ({
            ...l,
            netCents: parsed.get(l.id) ?? l.netCents,
        }));
        if (mode === 'credit') return edited.filter((l) => l.netCents !== 0);
        return buildCorrectionLines(invoice.lines, edited, () => crypto.randomUUID());
    }, [mode, serviceLines, parsed, invoice.lines]);

    const previewTotals = useMemo(() => calculateInvoiceTotals(previewLines), [previewLines]);

    const issues = useMemo(
        () => (mode === 'correction' ? validateCorrection(invoice, previewLines) : []),
        [mode, invoice, previewLines],
    );
    const blockers = issues.filter((m) => m.level === 'error');

    const canSubmit =
        !busy &&
        reason.trim().length > 0 &&
        unreadable.length === 0 &&
        previewLines.length > 0 &&
        blockers.length === 0;

    const submit = () => {
        const edited: InvoiceLine[] = serviceLines.map((l) => ({
            ...l,
            netCents: parsed.get(l.id) ?? l.netCents,
        }));
        if (mode === 'correction') onSubmitCorrection(edited, reason.trim());
        else onSubmitCredit(previewLines, reason.trim());
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 animate-fade-in backdrop-blur-sm p-4">
            <div className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
                    <h2 className="font-semibold text-slate-800">
                        Beleg berichtigen — {invoice.invoiceNumber}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded p-1 text-slate-400 hover:bg-slate-100"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="space-y-3 overflow-auto p-5">
                    <div className="flex gap-2">
                        {(
                            [
                                ['correction', 'Korrektur'],
                                ['credit', 'Gutschrift'],
                            ] as Array<[Mode, string]>
                        ).map(([key, label]) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setMode(key)}
                                className={`rounded-lg border px-3 py-1.5 text-sm ${
                                    mode === key
                                        ? 'border-slate-800 bg-slate-800 text-white'
                                        : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    <p className="text-sm text-slate-600">
                        {mode === 'correction'
                            ? 'Tragen Sie ein, wie die Positionen RICHTIG lauten müssen. Der Beleg enthält danach nur die Differenz — die ursprüngliche Rechnung bleibt bestehen.'
                            : 'Tragen Sie ein, welchen Betrag Sie je Position ERSTATTEN. Positionen mit 0,00 € bleiben außen vor.'}
                    </p>

                    <table className="w-full text-sm">
                        <thead className="text-left text-xs uppercase text-slate-500">
                            <tr>
                                <th className="py-1">Position</th>
                                <th className="py-1 text-right">bisher</th>
                                <th className="py-1 text-right">
                                    {mode === 'correction' ? 'richtig' : 'erstatten'}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {serviceLines.map((l) => (
                                <tr key={l.id}>
                                    <td className="py-1.5">
                                        {l.title}
                                        {l.lvPosition && (
                                            <span className="ml-1 text-slate-400">
                                                ({l.lvPosition})
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-1.5 text-right tabular-nums text-slate-500">
                                        {formatCents(l.netCents)}
                                    </td>
                                    <td className="py-1.5 text-right">
                                        <input
                                            inputMode="decimal"
                                            value={amounts[l.id] ?? ''}
                                            onChange={(e) =>
                                                setAmounts((a) => ({ ...a, [l.id]: e.target.value }))
                                            }
                                            className={`w-32 rounded border px-2 py-1 text-right tabular-nums ${
                                                parsed.get(l.id) == null
                                                    ? 'border-red-400 bg-red-50'
                                                    : 'border-slate-300'
                                            }`}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {unreadable.length > 0 && (
                        <p className="text-xs text-red-600">
                            {unreadable.length} Betrag/Beträge sind nicht lesbar. Beispiel: 1.234,56
                        </p>
                    )}

                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                        <p className="mb-1 font-medium text-slate-700">
                            {mode === 'correction'
                                ? 'Differenz auf dem Korrekturbeleg'
                                : 'Gutschrift'}
                        </p>
                        {previewLines.length === 0 ? (
                            <p className="text-slate-500">
                                {mode === 'correction'
                                    ? 'Keine Abweichung — es gibt nichts zu korrigieren.'
                                    : 'Kein Betrag erfasst.'}
                            </p>
                        ) : (
                            <ul className="space-y-0.5 text-xs">
                                {previewLines.map((l) => (
                                    <li key={l.id} className="flex justify-between">
                                        <span className="text-slate-600">{l.title}</span>
                                        <span className="tabular-nums">
                                            {formatCents(
                                                mode === 'credit' ? -l.netCents : l.netCents,
                                            )}
                                        </span>
                                    </li>
                                ))}
                                <li className="flex justify-between border-t border-slate-300 pt-1 font-semibold">
                                    <span>Brutto</span>
                                    <span className="tabular-nums">
                                        {formatCents(
                                            mode === 'credit'
                                                ? -previewTotals.grossTotalCents
                                                : previewTotals.grossTotalCents,
                                        )}
                                    </span>
                                </li>
                            </ul>
                        )}
                    </div>

                    {blockers.length > 0 && (
                        <ul className="space-y-0.5 text-xs text-red-600">
                            {blockers.map((m) => (
                                <li key={m.code} className="flex items-start gap-1.5">
                                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                    {m.message}
                                </li>
                            ))}
                        </ul>
                    )}

                    <label className="block text-sm">
                        <span className="mb-1 block font-medium text-slate-700">
                            Grund (Pflicht, steht auf dem Beleg)
                        </span>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={2}
                            className="w-full rounded border border-slate-300 px-2 py-1.5"
                        />
                    </label>

                    {fehler && (
                        <div
                            role="alert"
                            className="max-h-64 overflow-y-auto whitespace-pre-line rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900"
                        >
                            {fehler}
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
                    <Button variant="secondary" onClick={onClose} disabled={busy}>
                        Abbrechen
                    </Button>
                    <Button onClick={submit} disabled={!canSubmit}>
                        {busy ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird erstellt …
                            </>
                        ) : mode === 'correction' ? (
                            'Korrekturrechnung ausstellen'
                        ) : (
                            'Gutschrift ausstellen'
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};
